import { useState } from 'react';
import { documentsAPI, Document } from '../../lib/api';
import { getApiErrorMessage } from '../../lib/api/types/common';
import { useConfirm } from '../../contexts/ConfirmDialogContext';
import { toast } from 'sonner';

interface UseDocumentArchiveOptions {
  /** Refresh the page's documents after anything is archived or restored. */
  onChanged: () => void | Promise<void>;
  /** Called with a document's id once it is archived (e.g. to close its detail popup). */
  onArchived?: (id: number) => void;
}

type ArchivableDocument = Pick<Document, 'id' | 'title'>;

/**
 * Archive, restore and bulk-archive documents, with toast feedback.
 *
 * Archiving takes a document out of the folder and list views without deleting
 * it; the backend decides who may do it (the uploader, or a customer admin for
 * anything on their portal) and a refusal comes back as an error toast.
 *
 * @example
 * const archive = useDocumentArchive({ onChanged: fetchData });
 * <Button onClick={() => archive.archiveDocument(doc)}>Archive</Button>
 *
 * @param options - refresh callback and optional per-document archived callback
 * @returns archiveDocument, restoreDocument, archiveSelected and a busy flag
 */
export function useDocumentArchive({ onChanged, onArchived }: UseDocumentArchiveOptions) {
  const confirm = useConfirm();
  const [busy, setBusy] = useState(false);

  const restoreDocument = async (doc: ArchivableDocument): Promise<boolean> => {
    setBusy(true);
    try {
      await documentsAPI.unarchiveDocument(doc.id);
      toast.success(`Restored "${doc.title}"`);
      await onChanged();
      return true;
    } catch (err: unknown) {
      console.error('Error restoring document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to restore document'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const archiveDocument = async (doc: ArchivableDocument): Promise<boolean> => {
    setBusy(true);
    try {
      await documentsAPI.archiveDocument(doc.id);
      onArchived?.(doc.id);
      toast.success(`Archived "${doc.title}"`, {
        description: 'Find it under Archived to restore it.',
        action: { label: 'Undo', onClick: () => { void restoreDocument(doc); } },
      });
      await onChanged();
      return true;
    } catch (err: unknown) {
      console.error('Error archiving document:', err);
      toast.error(getApiErrorMessage(err, 'Failed to archive document'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  /** Archive every selected document after confirming. Resolves true when the request went through. */
  const archiveSelected = async (ids: number[]): Promise<boolean> => {
    if (ids.length === 0) return false;
    const noun = ids.length === 1 ? 'document' : 'documents';
    const ok = await confirm({
      title: `Archive ${ids.length} ${noun}`,
      description: `The selected ${noun} will leave the folders but stay available under Archived, where they can be restored.`,
      confirmLabel: 'Archive',
    });
    if (!ok) return false;
    setBusy(true);
    try {
      const { data } = await documentsAPI.bulkArchiveDocuments(ids);
      const done = data.archived.length;
      const refused = data.denied.length + data.not_found.length;
      if (done > 0) {
        toast.success(`Archived ${done} ${done === 1 ? 'document' : 'documents'}`);
      }
      if (refused > 0) {
        toast.error(`${refused} ${refused === 1 ? 'document was' : 'documents were'} not archived — you can only archive documents you are allowed to manage`);
      }
      data.archived.forEach((id) => onArchived?.(id));
      await onChanged();
      return true;
    } catch (err: unknown) {
      console.error('Error archiving documents:', err);
      toast.error(getApiErrorMessage(err, 'Failed to archive documents'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  return { archiveDocument, restoreDocument, archiveSelected, busy };
}
